import { useCallback } from "react";

import { v4 as uuidv4 } from "uuid";

import { ENDPOINT_PROETHICS_TURN } from "../constants";
import { SCENARIO_REGISTRY } from "../data/scenario-registry";
import { createSSEParser } from "../helpers/parseUIMessageStream";
import { streamProethicsPost } from "../helpers/proethicsApi";
import type { TranscriptMessage, TurnRequest } from "../types";
import { useProethicsFlowStore } from "./useProethicsFlowStore";

export const useProethicsConversation = () => {
	const transcript = useProethicsFlowStore((s) => s.transcript);
	const arcStage = useProethicsFlowStore((s) => s.arcStage);
	const isStreaming = useProethicsFlowStore((s) => s.isStreaming);

	const sendText = useCallback(async (raw: string) => {
		const store = useProethicsFlowStore;
		const text = raw.trim();
		if (!text || store.getState().isStreaming) {
			return;
		}
		store.setState({ isStreaming: true });

		// Mentor bubble appears immediately (we already have the text).
		const mentorMsg: TranscriptMessage = {
			id: uuidv4(),
			role: "mentor",
			text,
		};
		const priorTranscript = store.getState().transcript;
		store.setState({ transcript: [...priorTranscript, mentorMsg] });

		const scenarioId = store.getState().scenarioId;
		const avatarRole = SCENARIO_REGISTRY[scenarioId].avatarRole;

		// The avatar bubble is created lazily on the first reply token, so no
		// empty placeholder is shown while waiting.
		const avatarId = uuidv4();
		const upsertAvatar = (patch: Partial<TranscriptMessage>) =>
			store.setState((prev) => {
				if (prev.transcript.some((m) => m.id === avatarId)) {
					return {
						transcript: prev.transcript.map((m) =>
							m.id === avatarId ? { ...m, ...patch } : m,
						),
					};
				}
				return {
					transcript: [
						...prev.transcript,
						{ id: avatarId, role: avatarRole, text: "", ...patch },
					],
				};
			});

		const body: TurnRequest = {
			scenarioId,
			arcStage: store.getState().arcStage,
			transcript: priorTranscript.map(({ role, text: t }) => ({
				role,
				text: t,
			})),
			learnerInput: { modality: "text", text },
			threadId: store.getState().threadId ?? undefined,
		};

		// Audio arrives as ordered base64 chunks; assembled on `done` because
		// the expo-av player needs a complete data URI.
		const audioChunks: string[] = [];
		const feed = createSSEParser((part) => {
			switch (part.type) {
				case "data-thread-created":
					store.setState({ threadId: part.data.threadId });
					break;
				case "data-avatar-reply":
					upsertAvatar({ text: part.data.reply });
					break;
				case "data-avatar-audio-chunk":
					audioChunks.push(part.data.chunkBase64);
					break;
				case "data-avatar-audio-done": {
					const audioBase64 = audioChunks.join("");
					audioChunks.length = 0;
					if (audioBase64) {
						store.setState({ autoplayMessageId: avatarId });
						upsertAvatar({ audioBase64 });
					}
					break;
				}
				case "data-turn-state":
					store.setState({ arcStage: part.data.arcStage });
					break;
				default:
					break;
			}
		});

		try {
			await streamProethicsPost(ENDPOINT_PROETHICS_TURN, body, feed);
		} catch {
			upsertAvatar({
				text: "Sorry - something went wrong. Please try again.",
			});
		} finally {
			store.setState({ isStreaming: false });
		}
	}, []);

	return {
		transcript,
		arcStage,
		isStreaming,
		sendText,
	};
};

export type UseProethicsConversation = ReturnType<
	typeof useProethicsConversation
>;
